import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/cooler-cushion-rtic.png";
import heroPicture from "@/assets/cooler-cushion-rtic.png?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import { CoolerCushionForm } from "@/components/CoolerCushionForm";

export const Route = createFileRoute("/cooler-gun-gear-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Custom Cooler Wraps, Gun Covers & Hunting Gear Bags in Texas | Texas Made Shade",
      description:
        "Insulated YETI & RTIC cooler wraps, gun covers, bow & arrow covers, rod-rack covers, decoy bags and tie-down field bags — sewn in our Seguin.",
      image: hero,
      imageAlt: "Custom cooler cushion and wrap for a YETI / RTIC ice chest by Texas Made Shade",
      socialTitle: "Custom Cooler Wraps, Gun Covers & Hunting Gear Bags — Seguin, TX",
      socialDescription:
        "YETI / RTIC cooler wraps, gun & bow covers, decoy bags and gear totes — built in Seguin, Texas.",
      keywords:
        "custom YETI cooler covers, RTIC cooler wraps, ice chest cushions, gun covers, bow and arrow covers, rod rack covers, decoy bags, sandhill crane decoy bag, quail bag, dove bag, shell bag, gear tote, hunting gear bags, Seguin TX",
    }),
    scripts: serviceHeadScripts({
      path: "/cooler-gun-gear-covers",
      serviceName: "Cooler, Gun & Gear Covers",
      breadcrumbName: "Cooler & Gear Covers",
      description: "Custom cushions and protective covers for RTIC/Yeti coolers, gun cases and hunting gear — fabricated in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/cooler-gun-gear-covers"
      serviceName="Cooler, Gun & Gear Covers"
      breadcrumbs={[
        { label: "Custom Hunting & Fishing Covers", to: "/hunting-fishing-covers" },
        { label: "Cooler Wraps, Gun Covers & Hunting Gear Bags" },
      ]}
      eyebrow="★ COOLERS, GUN & GEAR ★"
      title="Cooler Wraps, Gun Covers, Bow Covers & Hunting Gear Bags"
      intro="Insulated YETI and RTIC cooler wraps, gun covers, bow and arrow covers, rod-rack covers, decoy bags and tie-down field bags — all sewn in-house on industrial machines with heavy bonded marine thread and reinforced stress points."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Sewn In-House · Seguin, TX",
        headline: "Need a Custom Cover, Wrap or Bag? Send Us the Specs.",
        subhead:
          "Tell us what you need covered — cooler size, gun model, bow type, decoy spread — and we'll come back with a written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Cooler / Gun / Gear Cover")}&notes=${encodeURIComponent("Cooler / gun / gear cover — please describe the item, size and any custom features (handles, pockets, padding).")}`,
        primaryLabel: "Get My Gear Cover Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "YETI- and RTIC-style insulated cooler wraps and ice-chest cushions",
        "Padded gun cases, bow & arrow covers and rod-rack covers",
        "Sandhill crane, quail and dove decoy bags",
        "Shell bags, gear totes and ranch tie-down bags",
        "Heavy bonded marine thread and reinforced stress points",
        "Custom one-off builds — bring us a sketch and we'll build it",
      ]}
      body={[
        {
          heading: "YETI & RTIC Cooler Wraps and Cushions",
          text: "Insulated wraps that keep ice longer and protect the gel coat in your boat or truck bed. We also build padded cooler cushions for bay boats, blinds and party barges.",
          bullets: [
            "Patterned to YETI Tundra, Roadie and RTIC sizes",
            "Reinforced corners and lid cutouts",
            "Marine-vinyl or Sunbrella exterior options",
          ],
        },
        {
          heading: "Gun Covers, Bow & Arrow Covers",
          text: "Padded covers built around your shotgun, rifle or bow with soft interior linings, marine zippers and reinforced handles. Sized to your specific gear so nothing slides or rattles.",
          bullets: [
            "Padded interiors with brushed lining",
            "YKK marine zippers and reinforced handles",
            "Embroidered initials available on request",
          ],
        },
        {
          heading: "Decoy Bags & Field Gear Totes",
          text: "Heavy-duty bags built to haul a full spread of sandhill crane, quail or dove decoys — plus shell bags, ranch tie-down totes and one-off custom hunting gear.",
          cta: { label: "Browse Hunting & Marine Fabrics", href: "/fabrics" },
        },
      ]}
      applicationsEyebrow="GEAR WE COVER"
      applicationsHeading="Gear We Cover"
      applications={[
        "YETI / RTIC Coolers",
        "Shotguns & Rifles",
        "Compound & Recurve Bows",
        "Rod Racks",
        "Sandhill Crane Decoys",
        "Quail & Dove Decoys",
        "Shell Bags",
        "Ranch Tie-Down Bags",
      ]}
      faqs={[
        {
          q: "Do you make custom YETI cooler covers, gun covers and bow & arrow covers?",
          a: "Yes — insulated YETI and RTIC cooler wraps, gun covers, bow and arrow covers, rod-rack covers, decoy bags and tie-down hunting gear bags are all sewn in our Seguin shop on industrial machines with heavy marine thread.",
        },
        {
          q: "Can you build a one-off custom bag from a sketch?",
          a: "Yes. Bring or send a sketch and dimensions and we'll pattern, cut and stitch it in-house. We've built one-off shell bags, decoy bags, e-collar pouches and lead loops for hunters across Texas.",
        },
        {
          q: "What fabric do you recommend for a cooler wrap?",
          a: "Sunbrella® Marine for UV stability or marine vinyl when you want maximum abrasion resistance. Both pair with insulated linings and reinforced corners.",
        },
      ]}
    >
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Request a Quote</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Tell Us About Your Cooler or Cushion
          </h2>
          <p className="mt-3 text-muted-foreground">
            Send dimensions and a photo and we'll come back with a written estimate.{" "}
            <Link to="/hunting-fishing-covers" className="font-semibold text-navy-deep underline">
              Or browse our full hunting cover lineup →
            </Link>
          </p>
          <div className="mt-8">
            <CoolerCushionForm />
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
